'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/hooks/use-auth'
import {
	buildNavEntries,
	isGroupOpen,
	type NavBadgeCounts,
	type NavEntry,
	type NavGroupState,
	openGroup,
	parseNavGroupState,
	toggleGroup,
} from '@/lib/nav-model'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'

/**
 * React adapter over the pure nav model in @/lib/nav-model.
 *
 * The structure itself (which entries exist, which group owns them) lives in
 * nav-model.ts so it stays testable in plain Node; this file supplies the
 * shared entries and the open/closed group state. Both <Sidebar> and
 * <MobileNav> render from these hooks so the two can never drift.
 */

export {
	badgeCount,
	badgeLabel,
	findActiveGroupId,
	flattenNavEntries,
	groupBadge,
	isNavGroup,
	type NavBadgeCounts,
	type NavEntry,
	type NavGroup,
	type NavItem,
} from '@/lib/nav-model'

const NAV_ENTRIES = buildNavEntries()

/** Shared desktop/mobile navigation, independent of analytics and rollout flags. */
export function useNavEntries(): NavEntry[] {
	return NAV_ENTRIES
}

/** Total findings awaiting the author's review — drives the Review badge. */
export function usePendingReviewCount(): number {
	const { isAuthenticated } = useAuth()
	const { data } = trpc.security.getMyFindingsSummary.useQuery(undefined, {
		enabled: isAuthenticated,
		staleTime: 5 * 60_000,
		refetchOnWindowFocus: false,
		retry: false,
	})
	return (data?.extensions ?? []).reduce(
		(sum: number, row: { remaining: number }) => sum + (row.remaining || 0),
		0,
	)
}

/**
 * Agent-proposed changes still waiting for a decision — drives the Approvals
 * badge. Same input as <PendingApprovalsCard>, so the two share one cached
 * query, and deciding on /approvals/[id] (which invalidates approval.list)
 * clears the badge too. `retry: false` keeps a BFF that predates approvals to
 * one failed request and no badge.
 */
export function usePendingApprovalsCount(): number {
	const { isAuthenticated } = useAuth()
	const { data } = trpc.approval.list.useQuery(
		{ waitingOnly: true },
		{ enabled: isAuthenticated, retry: false, refetchOnWindowFocus: true },
	)
	return data?.length ?? 0
}

/** Every nav counter at once, for the sidebar and the mobile drawer. */
export function useNavBadgeCounts(): NavBadgeCounts {
	const pendingReview = usePendingReviewCount()
	const pendingApprovals = usePendingApprovalsCount()
	return useMemo(() => ({ pendingReview, pendingApprovals }), [pendingReview, pendingApprovals])
}

const GROUPS_KEY = 'dojo.nav.groups'

/**
 * Per-group open/closed state, persisted across sessions and shared by the
 * sidebar and the mobile drawer (one localStorage key, so collapsing a group
 * on desktop keeps it collapsed on the phone).
 *
 * The map stores CLOSED groups, so an unknown id defaults to open: a newly
 * added group is visible to existing users instead of silently hidden, and the
 * pre-hydration render (empty map = everything open) matches SSR.
 */
export function useNavGroupState(activeGroupId: string | null) {
	const [closed, setClosed] = useState<NavGroupState>({})
	const [hydrated, setHydrated] = useState(false)

	useEffect(() => {
		try {
			setClosed(parseNavGroupState(localStorage.getItem(GROUPS_KEY)))
		} catch {
			// Storage itself unavailable (Safari private mode throws on access).
			// A readable-but-garbage value is already handled by the parser.
		}
		setHydrated(true)
	}, [])

	useEffect(() => {
		if (!hydrated) return
		try {
			localStorage.setItem(GROUPS_KEY, JSON.stringify(closed))
		} catch {
			// Private mode / quota — the state still works for this session.
		}
	}, [closed, hydrated])

	// A group must never hide the page the user is currently on. Gated on
	// `hydrated` so it runs AFTER the stored map lands, and keyed on the group
	// id (not the pathname) so moving between two pages of the same group does
	// not fight a deliberate collapse.
	useEffect(() => {
		if (!hydrated || !activeGroupId) return
		setClosed((prev) => openGroup(prev, activeGroupId))
	}, [hydrated, activeGroupId])

	const isOpen = useCallback((id: string) => isGroupOpen(closed, id), [closed])
	const toggle = useCallback((id: string) => {
		setClosed((prev) => toggleGroup(prev, id))
	}, [])

	return { isOpen, toggle }
}

/**
 * The pill-shaped nav counter. Shared by the sidebar, the mobile drawer and the
 * collapsed-group rollup so all three read identically. `label` is what a
 * screen reader hears — see badgeLabel().
 */
export function NavBadge({ count, label, active }: { count: number; label: string; active?: boolean }) {
	return (
		<span
			role="img"
			className={cn(
				'inline-flex items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none',
				active ? 'bg-primary-foreground text-primary' : 'bg-primary text-primary-foreground',
			)}
			aria-label={label}
		>
			{count > 99 ? '99+' : count}
		</span>
	)
}

const DOT_CLASS = 'absolute right-1 top-1 h-2 w-2 rounded-full bg-primary ring-2 ring-card'

/**
 * Dot form of <NavBadge>, for the icon-only collapsed rail.
 *
 * Omitting `label` makes it decorative, for the case where the surrounding
 * control already states the count in its own aria-label — a group trigger
 * announcing "Development, 3 findings…" must not then announce the dot too.
 */
export function NavBadgeDot({ label }: { label?: string }) {
	if (!label) return <span aria-hidden="true" className={DOT_CLASS} />
	return <span role="img" className={DOT_CLASS} aria-label={label} />
}
